import { useTranslation } from 'react-i18next';

import {
  AlertBlock,
  Button,
  ButtonsWrapper,
  Form,
  Loader,
  PasswordInput,
  PhoneNumberInput,
  SelectInput,
  TextInput,
  NewTimezoneSelect,
} from '@thinknet-gmbh/platform-common-ui';
import { PLATFORM_LANGUAGES } from '@thinknet-gmbh/platform-common';

import { useGetPermissionsGroups } from 'src/hooks/permission-group/get-permissions-groups.hook';
import { useUpdateCurrentProfile } from 'src/hooks/profiles/update-current-profile.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';

import { MainContainer } from './user-info-edit.styles';
import { useSendChangeEmailLink } from 'src/hooks/accounts/send-change-email-link.hook';
import { useState } from 'react';
import { getTimezoneValue } from '../../../../../../common/helpers/common.helpers';

const UserInfoEdit = ({ onCancel, data = {} }: { onCancel: () => void; data: any }) => {
  const { t } = useTranslation(['common', 'settings']);
  const { profile = {} } = data || {};
  const currentCompanyId = useGetCurrentCompanyId();
  const { permissionsGroups, loading } = useGetPermissionsGroups(currentCompanyId);
  const timezoneValue = getTimezoneValue(profile);
  const [timezone, setTimezone] = useState<string>(timezoneValue);
  const [timezoneOffset, setTimezoneOffset] = useState<number>(profile?.timezone || 120);

  const updateCurrentProfile = useUpdateCurrentProfile();
  const sendChangeEmailLink = useSendChangeEmailLink();

  const onTimeZoneSelect = (value: string, options: any) => {
    if (options?.dataoffset) {
      setTimezoneOffset(options.dataoffset);
    }
    setTimezone(value);
  };

  const onFinishHandler = (values: any) => {
    updateCurrentProfile({
      variables: {
        payload: {
          ...values.profile,
          timezone: timezoneOffset,
          timezoneFullFormat: timezone,
        },
      },
    });

    if (values.password) {
      sendChangeEmailLink({
        variables: {
          payload: {
            currentPassword: values.password,
            email: values.account.email,
          },
        },
      });
    }

    onCancel();
  };

  const newData = { ...data, profile: { ...profile, timezone: timezoneValue } };

  if (loading) {
    return <Loader />;
  }

  return (
    <Form layout="vertical" onFinish={onFinishHandler} initialValues={newData}>
      <MainContainer>
        <div>
          <Form.Item
            name={['profile', 'firstName']}
            label={t('common:labels.firstName')}
            rules={[{ required: true, message: t('common:validation.error.firstNameRequired') }]}
          >
            <TextInput disabled />
          </Form.Item>
          <Form.Item
            name={['profile', 'lastName']}
            label={t('common:labels.lastName')}
            rules={[{ required: true, message: t('common:validation.error.lastNameRequired') }]}
          >
            <TextInput disabled />
          </Form.Item>
          <Form.Item
            name={['profile', 'phoneNumber']}
            label={t('common:labels.phoneNumber')}
            rules={[{ required: true, message: t('common:validation.error.phoneNumberRequired') }]}
          >
            <PhoneNumberInput />
          </Form.Item>

          <Form.Item
            label={t('common:labels.email')}
            name={['account', 'email']}
            rules={[
              { required: true, message: t('common:validation.error.emailRequired') },
              { type: 'email', message: t('common:validation.error.incorrectFormat') },
            ]}
          >
            <TextInput maxLength={50} />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currValues) =>
              prevValues.account.email !== currValues.account.email
            }
          >
            {({ getFieldValue }) => {
              const emailIsChanged = getFieldValue(['account', 'email']) !== data.account.email;

              return (
                <>
                  <Form.Item
                    hidden={!emailIsChanged || !data.account.hasPassword}
                    rules={[{ required: emailIsChanged }]}
                    name="password"
                    label={t('settings:userProfileSettings.labels.passwordIfChangingEmail')}
                  >
                    <PasswordInput
                      placeholder={t('settings:userProfileSettings.placeholders.enterYourPassword')}
                    />
                  </Form.Item>
                  {emailIsChanged && !data.account.hasPassword && (
                    <AlertBlock autoWidth title={t('common:labels.warning')}>
                      {t('common:validation.error.emailChangeRequirePassword')}
                    </AlertBlock>
                  )}
                </>
              );
            }}
          </Form.Item>
        </div>
        <div>
          <Form.Item
            // name="companyName"
            label={t('settings:company.labels.companyName')}
            rules={[
              { required: true, message: t('settings:company.exceptions.requiredCompanyName') },
            ]}
          >
            <SelectInput disabled>{}</SelectInput>
          </Form.Item>
          <Form.Item
            // name="department"
            label={t('settings:userProfileSettings.labels.department')}
            rules={[
              {
                required: true,
                message: t('settings:userProfileSettings.validations.departmentRequired'),
              },
            ]}
          >
            <SelectInput disabled>{}</SelectInput>
          </Form.Item>
          <Form.Item
            // name="jobTitle"
            label={t('settings:userProfileSettings.labels.jobTitle')}
            rules={[
              {
                required: true,
                message: t('settings:userProfileSettings.validations.jobTitleRequired'),
              },
            ]}
          >
            <TextInput disabled />
          </Form.Item>
          <Form.Item
            // name="managerName"
            label={t('settings:userProfileSettings.labels.inObeysOf')}
          >
            <TextInput disabled />
          </Form.Item>
        </div>
        <div>
          <Form.Item
            name={['account', 'permissionGroupId']}
            label={t('common:labels.permission')}
            rules={[
              { required: true, message: t('common:validation.error.permissionGroupRequired') },
            ]}
          >
            <SelectInput disabled>
              {permissionsGroups.map((permission: any) => (
                <SelectInput.Option key={permission._id} value={permission._id}>
                  {permission.name}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>
          <Form.Item
            name={['profile', 'language']}
            label={t('settings:userProfileSettings.labels.chooseYourLanguage')}
            rules={[{ required: true, message: t('common:validation.error.languageRequired') }]}
          >
            <SelectInput>
              <SelectInput.Option value={PLATFORM_LANGUAGES.en}>
                {t('common:languages.en')}
              </SelectInput.Option>
              <SelectInput.Option value={PLATFORM_LANGUAGES.de}>
                {t('common:languages.de')}
              </SelectInput.Option>
            </SelectInput>
          </Form.Item>
          <Form.Item
            name={['profile', 'timezone']}
            label={t('common:labels.timeZone')}
            rules={[{ required: true, message: t('common:validation.error.timezoneRequired') }]}
          >
            <NewTimezoneSelect value={timezone} onSelect={onTimeZoneSelect} />
          </Form.Item>
        </div>
      </MainContainer>
      <ButtonsWrapper marginTop={16}>
        <Button type="primary" htmlType="submit">
          {t('common:buttons.change')}
        </Button>
        <Button onClick={onCancel}>{t('common:buttons.cancel')}</Button>
      </ButtonsWrapper>
    </Form>
  );
};

export default UserInfoEdit;
